<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div
    :id="id"
    class="daypart-editor-container"
    :class="{ compact: isCompact || isAvailableWidthReduced, readonly: isReadOnly }">
    <div class="daypart-editor">
      <div
        v-if="showEmptyDaypartWarning"
        class="alert alert-warning">
        <icon-warning />
        <p>{{ $t('messages.daypartNotEmpty') }}</p>
      </div>
      <table class="table table-bordered">
        <thead>
          <tr>
            <th id="toggle-week-button">
              <button
                v-if="!isWeekFull"
                id="book-full-week"
                @click="onWeekClicked">
                <icon-colored-plus />
              </button>
              <button
                v-else
                id="clear-week"
                class="reversed"
                @click="onWeekClicked">
                <icon-colored-minus />
              </button>
            </th>
            <template
              v-for="(hour, hourIndex) in days[0].hours"
              :key="`th-${hour}`">
              <th>
                <div class="angled-text-container">
                  <span
                    ><b>{{ hour.compactLabel }}</b>
                    <sub v-if="hour.compactLabelSuffix !== ''"> {{ hour.compactLabelSuffix }}</sub>
                  </span>
                </div>
                <button
                  v-if="!areAllHoursSelected(hourIndex)"
                  @click="onHourClicked(hourIndex)">
                  <icon-colored-plus />
                </button>
                <button
                  v-else
                  class="reversed"
                  @click="onHourClicked(hourIndex)">
                  <icon-colored-minus />
                </button>
              </th>
            </template>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(day, dayIndex) in days"
            :key="dayIndex">
            <th>
              <span>{{ day.label }}</span>
              <button
                v-if="!isDayFull(dayIndex)"
                @click="onDayClicked(dayIndex)">
                <icon-colored-plus />
              </button>
              <button
                v-else
                class="reversed"
                @click="onDayClicked(dayIndex)">
                <icon-colored-minus />
              </button>
            </th>
            <td
              v-if="dayIndex === 0"
              colspan="24"
              rowspan="7"
              style="padding: 0"
              class="canvas-cell">
              <DaypartGrid
                :schedule="weekSchedule"
                :time-interval="usePreciseDaypart ? 'quarter' : 'hour'"
                @area-selected="onAreaSelected"
                @range-resized="updateState" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { DateTime } from 'luxon';
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

  import { IconColoredMinus, IconColoredPlus } from '@/common/icons/colored';
  import { IconWarning } from '@/common/icons/monochrome';
  import Day from '@/common/models/Day';
  import { HOUR_RANGES } from '@/common/models/daypart/DaySchedule';
  import WeekSchedule, { IDaypartRange, IRange, TimeUnit } from '@/common/models/daypart/WeekSchedule';
  import GlobalEventBus from '@/GlobalEventBus';
  import GlobalEvents from '@/GlobalEvents';
  import { FullDaypart, generateUUID, isDaypartEmpty } from '@/globals';

  import DaypartGrid from './DaypartGrid.vue';

  enum EventListener {
    RESIZE = 'resize',
  }

  interface IProps {
    daypart: string;
    isVisible?: boolean;
    isCompact?: boolean;
    isReadOnly?: boolean;
    usePreciseDaypart?: boolean;
  }

  const props = withDefaults(defineProps<IProps>(), {
    daypart: FullDaypart,
    isVisible: true,
    isCompact: false,
    isReadOnly: false,
    usePreciseDaypart: false,
  });

  const emits = defineEmits(['changed']);

  const id = `daypart_editor_${generateUUID()}`;
  const days: Day[] = [...Array(7).keys()].map((_, i) => new Day(DateTime.local().startOf('week').plus({ days: i })));

  const weekSchedule = ref(new WeekSchedule());
  const isAvailableWidthReduced = ref(false);
  const isInitialized = ref(false);
  const internalDaypartValue = ref<string | null>(null);

  const timeUnit = computed<TimeUnit>(() => (props.usePreciseDaypart ? TimeUnit.MINUTE : TimeUnit.HOUR));
  const isWeekFull = computed<boolean>(
    () => !!weekSchedule.value.daySchedules.every((daySchedule) => daySchedule.isFull()),
  );
  const showEmptyDaypartWarning = computed<boolean>(
    () => internalDaypartValue.value !== null && isDaypartEmpty(internalDaypartValue.value),
  );

  function updateState(): void {
    internalDaypartValue.value = weekSchedule.value.toString(timeUnit.value);
    emits('changed', internalDaypartValue.value);
  }

  function initDaypartEditor(): void {
    window.addEventListener(EventListener.RESIZE, checkAvailableWidth);
    GlobalEventBus.$on(GlobalEvents.navbarToggled, checkAvailableWidth);
    checkAvailableWidth();
    isInitialized.value = true;
  }

  function isDayFull(dayIndex: number): boolean {
    return weekSchedule.value.daySchedules[dayIndex].isFull();
  }

  function areAllHoursSelected(hourIndex: number): boolean {
    const hourRange = HOUR_RANGES[hourIndex];
    return weekSchedule.value.daySchedules.every((daySchedule) => !daySchedule.isRangeFree(hourRange));
  }

  function scheduleRange(range: IDaypartRange, select: boolean): void {
    if (select) {
      weekSchedule.value.scheduleRange(range);
    } else {
      weekSchedule.value.freeRange(range);
    }
    updateState();
  }

  function getContainerWidth(selector: string): number {
    const element = document.querySelector(selector);
    return element ? element.clientWidth : 0;
  }

  function onAreaSelected(range: IDaypartRange, isSelecting: boolean): void {
    scheduleRange(range, isSelecting);
  }

  function checkAvailableWidth(): void {
    const width = getContainerWidth('.daypart-editor-container');
    isAvailableWidthReduced.value = width < 935;
  }

  async function onHourClicked(hourIndex: number): Promise<void> {
    const timeRange = HOUR_RANGES[hourIndex];
    const dayRange: IRange = { from: 0, to: 6 };
    const range: IDaypartRange = {
      dayRange,
      timeRange,
    };
    const select = !areAllHoursSelected(hourIndex);
    scheduleRange(range, select);
  }

  function onDayClicked(dayIndex: number): void {
    if (days.length <= dayIndex) {
      return;
    }
    if (!isDayFull(dayIndex)) {
      weekSchedule.value.daySchedules[dayIndex].bookFullDay();
    } else {
      weekSchedule.value.daySchedules[dayIndex].clearDay();
    }
    updateState();
  }

  function onWeekClicked(): void {
    if (!isWeekFull.value) {
      weekSchedule.value.bookFullWeek();
    } else {
      weekSchedule.value.clearWeek();
    }
    updateState();
  }

  watch(
    () => props.daypart,
    (current) => {
      if (current !== internalDaypartValue.value) {
        weekSchedule.value = WeekSchedule.parse(props.daypart);
      }
    },
  );

  onMounted(() => {
    weekSchedule.value = WeekSchedule.parse(props.daypart);
    if (props.isVisible) {
      initDaypartEditor();
    }
  });

  onBeforeUnmount(() => {
    window.removeEventListener(EventListener.RESIZE, checkAvailableWidth);
  });
</script>

<style lang="scss" rel="stylesheet/scss">
  @use '../../../../styles/variables.scss';

  #app {
    .daypart-editor-container {
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      -khtml-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;

      thead,
      tbody {
        th {
          color: var(--actionsColor);

          cursor: pointer;
          position: relative;

          button {
            background: var(--successFaded75);

            outline: none;
            display: none;
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: none;
            border: none;
            font-weight: 900;
            width: 100%;
            font-size: 2.6rem;
            text-align: center;
            cursor: pointer;

            &.reversed {
              background: var(--errorFaded75);
            }
          }

          &:hover {
            /*background: var(--actionsColor);*/

            button {
              display: block;
            }

            span {
              opacity: 0;
            }
          }

          sub {
            font-weight: 200;
          }

          .angled-text-container {
            span {
              display: flex;
              flex-direction: column;
              align-items: center;
              transform: none;

              b {
                margin-top: 0.3rem;
              }

              sub {
                margin-top: 0.3rem;
                margin-bottom: 0.6rem;
              }
            }
          }
        }
      }

      thead {
        th {
          width: 3.75%;

          &:hover {
            padding: 0;
          }
        }
      }

      th {
        color: var(--textColor);

        font-weight: 400;
        font-size: 0.9em;
        vertical-align: middle;

        button {
          padding: 0.2rem 0;
          margin: 0;
          line-height: 0;
          vertical-align: middle;

          svg {
            width: 2rem;
            height: 2rem;
          }
        }
      }

      &.compact {
        th {
          /*padding: 0;*/
          font-size: 0.75em !important;

          button {
            padding: 0;

            svg {
              width: 1.2rem !important;
              height: 1.2rem !important;
            }
          }
        }

        thead {
          th {
            padding: 0rem 0 0.8rem !important;
            font-size: 1rem;
            border-bottom: 0.1rem;
          }
        }

        tbody {
          th {
            button {
              padding: 0.3rem 0;
            }
          }
        }

        tfoot {
          th {
            padding: 0.1rem 0 0.3rem;
          }
        }
      }

      .daypart-editor {
        width: 100%;
      }

      .daypart_editor_canvas {
        margin-bottom: -0.6rem;
        background: #ccc;
        width: 100%;
      }

      .canvas-cell {
        position: relative;

        &.selected {
          background-color: var(--primaryColor);
        }
      }

      &.readonly {
        cursor: not-allowed !important;

        .daypart-editor {
          pointer-events: none;
        }
      }
    }
  }

  .alert-warning {
    display: flex;
    align-items: center;

    svg {
      height: 1.6rem;
    }

    p {
      margin: 0 0 0 1rem;
    }
  }
</style>
